import React, { useState, useEffect } from 'react'
import {
  Box,
  TextField,
  Typography,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Button,
  Chip,
  InputAdornment,
} from '@mui/material'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import type { RangeFilter } from './types'

export interface NumericRangeFilterProps {
  /** Label key for this filter */
  labelKey: string
  /** Display name for the filter */
  displayName?: string
  /** Current range filter value */
  value: RangeFilter | undefined
  /** Called when the filter changes */
  onChange: (value: RangeFilter | undefined) => void
  /** Sample values from the data (legacy: min/max hint parsed from label strings) */
  sampleValues?: string[]
  /** Real min/max bounds (entity metrics) — preferred over sampleValues */
  bounds?: { min: number; max: number }
  /** Render the value as a percentage */
  isPercent?: boolean
  /** Whether the filter is disabled */
  disabled?: boolean
}

/**
 * NumericRangeFilter Component
 *
 * Provides min/max input fields for filtering numeric label values.
 * Supports values with %, commas, etc.
 */
export const NumericRangeFilter: React.FC<NumericRangeFilterProps> = ({
  labelKey,
  displayName,
  value,
  onChange,
  sampleValues,
  bounds,
  isPercent,
  disabled = false,
}) => {
  // Local state for input values (strings for controlled inputs)
  const [minInput, setMinInput] = useState<string>(value?.min?.toString() ?? '')
  const [maxInput, setMaxInput] = useState<string>(value?.max?.toString() ?? '')

  // Sync local state with prop value
  useEffect(() => {
    setMinInput(value?.min?.toString() ?? '')
    setMaxInput(value?.max?.toString() ?? '')
  }, [value])

  // Determine if values are percentages
  const hasPercentages = isPercent ?? (sampleValues?.some(v => v.includes('%')) ?? false)

  // Prefer real bounds (entity metrics); fall back to parsing sample values
  const dataRange = React.useMemo(() => {
    if (bounds) return bounds
    if (!sampleValues || sampleValues.length === 0) return null
    const numbers = sampleValues
      .map(v => parseFloat(v.replace(/%/g, '').replace(/,/g, '')))
      .filter(n => !isNaN(n))
    if (numbers.length === 0) return null
    return {
      min: Math.min(...numbers),
      max: Math.max(...numbers),
    }
  }, [bounds, sampleValues])

  const handleMinChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setMinInput(e.target.value)
  }

  const handleMaxChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setMaxInput(e.target.value)
  }

  const handleApply = () => {
    const min = minInput ? parseFloat(minInput) : undefined
    const max = maxInput ? parseFloat(maxInput) : undefined

    if (min === undefined && max === undefined) {
      onChange(undefined)
    } else {
      onChange({
        min: min !== undefined && !isNaN(min) ? min : undefined,
        max: max !== undefined && !isNaN(max) ? max : undefined,
      })
    }
  }

  const handleClear = () => {
    setMinInput('')
    setMaxInput('')
    onChange(undefined)
  }

  const hasValue = value?.min !== undefined || value?.max !== undefined

  return (
    <Accordion disableGutters elevation={0}>
      <AccordionSummary
        expandIcon={<ExpandMoreIcon />}
        sx={{
          '& .MuiAccordionSummary-content': {
            alignItems: 'center',
            gap: 1,
          },
        }}
      >
        <Typography sx={{ fontWeight: 500, fontSize: '0.875rem', textTransform: 'capitalize' }}>
          {displayName || labelKey}
        </Typography>
        {hasValue && (
          <Chip
            label={`${value?.min ?? '...'} - ${value?.max ?? '...'}`}
            size="small"
            color="secondary"
            sx={{ height: 20, fontSize: '0.75rem' }}
          />
        )}
      </AccordionSummary>
      <AccordionDetails sx={{ pt: 0 }}>
        {dataRange && (
          <Typography variant="caption" color="text.secondary" sx={{ mb: 1, display: 'block' }}>
            Data range: {dataRange.min.toLocaleString()}{hasPercentages ? '%' : ''} - {dataRange.max.toLocaleString()}{hasPercentages ? '%' : ''}
          </Typography>
        )}

        <Box sx={{ display: 'flex', gap: 1, mb: 1 }}>
          <TextField
            label="Min"
            size="small"
            type="number"
            value={minInput}
            onChange={handleMinChange}
            disabled={disabled}
            placeholder={dataRange?.min.toString()}
            InputProps={{
              endAdornment: hasPercentages ? (
                <InputAdornment position="end">%</InputAdornment>
              ) : undefined,
            }}
            sx={{ flex: 1 }}
          />
          <TextField
            label="Max"
            size="small"
            type="number"
            value={maxInput}
            onChange={handleMaxChange}
            disabled={disabled}
            placeholder={dataRange?.max.toString()}
            InputProps={{
              endAdornment: hasPercentages ? (
                <InputAdornment position="end">%</InputAdornment>
              ) : undefined,
            }}
            sx={{ flex: 1 }}
          />
        </Box>

        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button
            size="small"
            variant="contained"
            color="info"
            onClick={handleApply}
            disabled={disabled}
          >
            Apply
          </Button>
          <Button
            size="small"
            variant="text"
            onClick={handleClear}
            disabled={disabled || !hasValue}
          >
            Clear
          </Button>
        </Box>
      </AccordionDetails>
    </Accordion>
  )
}

export default NumericRangeFilter
